import { useAuthStore } from '@/store/auth.store';
import * as Sentry from '@sentry/react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface ApiOptions extends RequestInit {
  token?: string;
  _isRetry?: boolean;
}

class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: Record<string, string[]>,
    public errorCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Refresh lock: only one refresh at a time; concurrent 401s wait for the same refresh
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  const { refreshToken, isImpersonating, stopImpersonation } = useAuthStore.getState();

  // During impersonation there's no refresh token — restore admin session
  if (isImpersonating) {
    stopImpersonation();
    return false;
  }

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { setTokens, logout } = useAuthStore.getState();
    if (!refreshToken) {
      logout();
      return false;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        logout();
        return false;
      }

      const data = await res.json();
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      return true;
    } catch {
      logout();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { token, _isRetry, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // On 401, try to refresh the token and retry once
  if (response.status === 401 && token && !_isRetry) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const newToken = useAuthStore.getState().accessToken;
      return request<T>(endpoint, { ...options, token: newToken ?? undefined, _isRetry: true });
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    const apiError = new ApiError(response.status, error.message, error.details, error.error);

    // Report 5xx errors to Sentry (skip 4xx — those are expected business errors)
    if (response.status >= 500) {
      Sentry.captureException(apiError, {
        tags: { endpoint, method: fetchOptions.method || 'GET' },
      });
    }

    throw apiError;
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
}

export const api = {
  get: <T>(endpoint: string, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export { ApiError };
